// Core

import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { v4 as uuid } from 'uuid';
import { jsPDF } from 'jspdf';
import { useScreenshot } from 'use-react-screenshot';
import { useBooleanFlagValue } from '@rs-core/hooks/useFlags';

// MUI
import Drawer from '@mui/material/Drawer';

// Custom
import { FormTool } from '@worklist-2/ui/src/components/FormTool';
import { useConfig } from '@rs-core/context/ConfigContext';
import { mediaDesktopL, useIsTablet } from '@rs-core/utils/responsiveUtils';
import { useAppModeContext } from '@rs-core/context/AppModeContext';
import { useBlumeFormContext } from '@worklist-2/core/src/context/BlumeFormContext';
import { uploadFileToServer } from '@rs-core/utils/file';
import formUtils from '@rs-ui/views/utils/formUtils';
import pdfUtil from '@worklist-2/core/src/utils/pdfUtil';
import MobileBottomDrawer from '@worklist-2/patientPortal/src/components/MobileBottomDrawer/MobileDrawer/MobileBottomDrawer';

const DocumentReferenceJson = {
	resourceType: 'DocumentReference',
	masterIdentifier: {
		value: '',
	},
	status: 'current',
	type: {
		coding: [
			{
				code: '56444-3',
				display: 'Healthcare communication Document',
			},
		],
	},
	category: [
		{
			coding: [
				{
					code: '56444-3',
					display: 'Healthcare communication Document',
				},
			],
		},
	],

	subject: {
		id: '',
		reference: 'Patient/',
	},
	content: [
		{
			attachment: {
				contentType: 'application/pdf',
				data: '',
			},
		},
	],

	context: {
		related: [
			{
				id: '',
				reference: 'ImagingStudy/',
			},
			{
				id: '',
				reference: 'Appointment/',
			},
		],
	},
};

const FormDrawer = ({
	data,
	open,
	setOpen = () => {},
	updateForm,
	appointment,
	showCloseBtn = true,
	showBackBtn = false,
	autoSave = true,
	isOrderForm = false,
	canEditWhenCompleted = false,
	useFhirUrl = false,
	anchor = 'right',
	drawerColor = '#f8f8f8',
	sectionName = 'Page',
	fromHistory = false,
	userSignature = null,
	isCompact = false,
	pdfUrl,
	setPdfUrl,
	setNumOfRequiredNotFilled = () => {},
	onClose = () => {},
	onDrawer,
	onSubmit,
	orderFormMapper,
	drawerWidth,
	pdfWidth,
	pdfHeight = '85vh',
	submitButtonContainerSx,
	onEditClick,
	showEdit,
}) => {
	// Flags
	const mavenTechFormMandatory = useBooleanFlagValue('maven-1720-techform-mandatory');

	const isTablet = useIsTablet();
	const __config = useConfig();
	const [formData, setFormData] = useState(data?.resource);
	const [lastSyncedForm, setLastSyncedForm] = useState(null);
	const [progressPercentage, setProgressPercentage] = useState(0);
	const [validateFormData, setValidateFormData] = useState(false);
	const [isValidForm, setIsValidForm] = useState(false);
	const ref = useRef(null);
	const pageRef = useRef([]);
	const [studyId, setStudyId] = useState(null);
	const [appointmentId, setAppointmentId] = useState(null);
	const [patientId, setPatientId] = useState(null);
	const [, takeScreenshot] = useScreenshot();
	const { pdfInstance, pdfDataRef } = useBlumeFormContext();
	const isPdfForm = !!data?.pdfFileName;
	const [submitInProgress, setSubmitInProgress] = useState(false);
	const { isPatientPortalMode } = useAppModeContext();
	const appModeIsPatientPortal = isPatientPortalMode();

	const closeDrawer = () => {
		onClose();
		setOpen(false);
	};
	useEffect(() => {
		const participant = appointment?.participant;
		if (participant) {
			const id = participant
				?.find(item => item?.actor?.reference.toLowerCase().includes('patient'))
				?.actor.reference.split('/')[1];

			setPatientId(id);
		}

		const supportingInformation = appointment?.supportingInformation;
		if (supportingInformation) {
			setStudyId(supportingInformation[0]?.id);
		}

		if (appointment?.id) {
			setAppointmentId(appointment?.id);
		}
	}, [appointment]);

	/**
	 * Enable input form
	 */
	useEffect(() => {
		if (pdfInstance) {
			const { annotationManager } = pdfInstance.Core;
			annotationManager.disableReadOnlyMode();
		}
	}, [pdfInstance]);

	useEffect(
		() => async () => {
			if (
				mavenTechFormMandatory &&
				!fromHistory &&
				data?.id &&
				data?.pdfFileName &&
				!data?.completed &&
				pdfDataRef?.current &&
				!appModeIsPatientPortal
			) {
				if (data.study && data.patient) {
					const updateFormId = formData.template ? uuid() : formData.id;
					// Save pdf
					await pdfUtil?.uploadData(
						pdfDataRef.current,
						`${__config.data_sources.fhir}/Form/attachment/${updateFormId}?isReplaceFile=true`,
						formData?.pdfFileName
					);

					// Save form info in case new form generated
					if (formData.template) {
						// Generate new form
						delete formData.id;
						formData.id = updateFormId;
						formData.study = data.study;
						formData.template = false;
						await axios.post(`${__config.data_sources.fhir}/Form`, formData);
					}
				}
			}
		},
		[data]
	);

	useEffect(() => {
		if (data?.questionData?.length > 0) {
			pageRef.current = pageRef.current.slice(0, data.questionData.length);
		}
	}, [data?.questionData]);

	const getPageImages = () => {
		const imageArray = [];

		const promises = pageRef.current.map((e, index) =>
			takeScreenshot(e).then(img => imageArray.push({ index, image: img }))
		);

		return Promise.all(promises).then(() => imageArray.sort((a, b) => parseInt(a.index) - parseInt(b.index)));
	};

	useEffect(() => {
		// calculate total number of questions and check if they have answers
		if (formData && !isPdfForm) {
			let totalRequiredQues = null;
			let answerRequiredQues = 0;
			let subAnswerQuestions = 0;
			let hasAnswer;
			const allQuestions = formData?.formTemplate?.content.flat(1);
			allQuestions.forEach(question => {
				// reset flag per question
				hasAnswer = false;
				if (
					question?.type === 'multipleChoiceGrid' ||
					question?.type === 'checkboxGrid' ||
					question?.type === 'shortAnswer'
				) {
					subAnswerQuestions = 0;
					question?.options?.forEach((eachQ, index) => {
						if (
							question?.answer &&
							question?.answer[question?.answer?.length - 1]?.value[index]?.length > 0
						) {
							subAnswerQuestions += 1;
						}
					});
					hasAnswer = question?.options?.length == subAnswerQuestions;
				} else if (question?.type === 'checkbox') {
					if (
						question?.answer &&
						question?.answer[question?.answer?.length - 1]?.value?.filter(ans => ans.checked).length > 0
					) {
						hasAnswer = true;
					}
				} else if (question?.type === 'multipleChoice') {
					let answerFlag = false;
					if (question.answer) {
						question.answer[question?.answer?.length - 1].value?.forEach(v => {
							if (v.checked) {
								answerFlag = true;
							}
						});
					}

					hasAnswer = answerFlag;
				} else if (
					question?.answer &&
					question?.answer[question?.answer?.length - 1]?.value &&
					question?.answer[question?.answer?.length - 1]?.value !== ''
				) {
					hasAnswer = true;
				}

				// logic to enable/disable Submit button
				// Submit button should not be enabled until all required info has been entered
				if (question?.required) {
					totalRequiredQues += 1;

					if (hasAnswer) {
						answerRequiredQues += 1;
					}
				}
			});
			if (fromHistory) {
				setNumOfRequiredNotFilled(totalRequiredQues - answerRequiredQues);
			}
			setProgressPercentage((answerRequiredQues / totalRequiredQues) * 100);
			setIsValidForm(!totalRequiredQues || totalRequiredQues == answerRequiredQues);
		}
	}, [formData]);

	const answerList = ({ answerObj, patient, value, index, ...addnValue }) => {
		if (answerObj?.answer) {
			const updatedList = answerObj.answer.map(answer => {
				if (answer.submitterId === patient?.id) {
					if (['multipleChoiceGrid', 'shortAnswer'].includes(answerObj.type)) {
						const valueList = answer.value;
						valueList[index] = value;
						return {
							...answer,
							value: valueList,
							submitterDate: new Date().toISOString(),
							...addnValue,
						};
					}
					return {
						...answer,
						value,
						submitterDate: new Date().toISOString(),
						...addnValue,
					};
				}
				return answer;
			});
			const foundItem = updatedList.find(answer => answer.submitterId === patient?.id);
			if (foundItem) {
				return updatedList;
			}
			if (['multipleChoiceGrid', 'shortAnswer'].includes(answerObj.type)) {
				const valueObj = Array(answerObj.options?.length).fill(null);
				valueObj[index] = value;
				return [
					...updatedList,
					answerJson({
						patient,
						value: valueObj,
						...addnValue,
					}),
				];
			}
			return [
				...updatedList,
				answerJson({
					patient,
					value,
					...addnValue,
				}),
			];
		}
		return [answerJson({ patient, value, ...addnValue })];
	};

	const answerJson = ({ patient, value, ...addnValue }) => ({
		submitter: patient?.display,
		submitterType: 'patient',
		submitterId: patient?.id,
		submitterDate: new Date().toISOString(),
		value,
		...addnValue,
	});

	const handleFormData = async (key, index, value) => {
		if (!lastSyncedForm && autoSave) {
			autoSaveDebounce(formData);
		}
		const tempFormObj = { ...formData };
		const questionData = tempFormObj?.formTemplate?.content;
		let pageIndex = -1;
		let questionIndex = -1;
		for (let row_idx = 0; row_idx < questionData?.length; row_idx++) {
			const row = questionData[row_idx];
			const col_idx = row.findIndex(obj => obj.unique_id === key);
			if (col_idx !== -1) {
				pageIndex = row_idx;
				questionIndex = col_idx;
				break;
			}
		}
		const updateAnswer = questionData[pageIndex][questionIndex];
		if (['multipleChoice', 'checkbox'].includes(updateAnswer.type)) {
			updateAnswer.answer = answerList({
				answerObj: updateAnswer,
				patient: data.patient,
				...value,
			});
		}
		if (['signature', 'dropdown', 'date', 'paragraph', 'checkboxGrid'].includes(updateAnswer.type)) {
			updateAnswer.answer = answerList({
				answerObj: updateAnswer,
				patient: data.patient,
				value,
			});
		}

		if (['multipleChoiceGrid', 'shortAnswer'].includes(updateAnswer.type)) {
			const valueObj = Array(updateAnswer.options?.length).fill(null);
			valueObj[index] = value;
			updateAnswer.answer = answerList({
				answerObj: updateAnswer,
				patient: data.patient,
				value: updateAnswer?.answer ? value : valueObj,
				index,
			});
		}
		let validatedFormDataObj = tempFormObj;
		if (validateFormData) {
			validatedFormDataObj = await formUtils.validateForm(tempFormObj);
		}

		setFormData(validatedFormDataObj);
	};

	const removeErrorAttribute = async formDataObj => {
		const tempFormObj = { ...formDataObj };
		const allQuestions = tempFormObj?.formTemplate?.content.flat(1);
		allQuestions.forEach(question => {
			delete question?.error;
		});
		return tempFormObj;
	};

	const saveDocumentReference = (studyId, patientId, appointmentId) => {
		const leftMargin = 30;
		const topMargin = 10;
		const doc1 = new jsPDF('p', 'pt', 'a4', true);
		let doc2;

		const width = doc1.internal.pageSize.getWidth() + 60;
		const height = doc1.internal.pageSize.getHeight();
		const ratio = height / (width - 60);

		getPageImages().then(pageImgArray => {
			pageImgArray.forEach(({ index, image: pageImg }) => {
				const imgProps = doc1.getImageProperties(pageImg);

				const imgRatio = imgProps.height / imgProps.width;
				const resize = imgRatio > ratio;

				const pageWidth = width;
				const pageHeight = resize ? (width * imgProps.height) / imgProps.width : height;

				if (index === 0) {
					doc2 = new jsPDF('p', 'pt', [pageWidth, pageHeight], true);
				} else {
					doc2.addPage([pageWidth, pageHeight]);
				}

				doc2.addImage(
					pageImg,
					'PNG',
					leftMargin,
					topMargin,
					pageWidth - leftMargin * 2,
					(resize ? pageHeight : (imgProps.height * width) / imgProps.width) - topMargin * 2,
					undefined,
					'FAST'
				);
			});

			const blob = doc2.output('datauristring').split(',')[1];

			if (studyId && patientId && appointmentId && blob) {
				const documentReferenceTosave = JSON.parse(JSON.stringify(DocumentReferenceJson));

				documentReferenceTosave.subject.id = patientId;
				documentReferenceTosave.subject.reference += patientId;

				documentReferenceTosave.context.related[0].id = studyId;
				documentReferenceTosave.context.related[0].reference += studyId;

				documentReferenceTosave.context.related[1].id = appointmentId;
				documentReferenceTosave.context.related[1].reference += appointmentId;

				documentReferenceTosave.content[0].attachment.data = blob;

				axios.post(`${__config.data_sources.blume}DocumentReference`, documentReferenceTosave).then(result => {
					if (result?.status === 200) {
						console.log('successfully saved document reference', result);
					} else {
						console.error('failed to save document reference');
					}
				});
			}
		});
	};

	const processOrderSaveData = (entry, formData) => {
		if (entry?.length > 0) {
			const serviceRequest = entry.find(r => r?.resource?.resourceType === 'ServiceRequest')?.resource;
			const studyId = serviceRequest?.code?.coding[0]?.id;
			const patientId = serviceRequest?.subject?.id;

			if (studyId && patientId) {
				formData.referenceTemplate = formData.id;
				delete formData.id;
				formData.template = false;
				formData.patient = {
					id: patientId,
				};
				formData.study = {
					id: studyId,
				};

				axios
					.post(`${__config.data_sources.fhir}/Form`, formData)
					.then(async result => {
						if (result.status == '201') {
							console.log('Saving Order Form successfully');
							// Save pdf answer
							isPdfForm && (await savePdfForm(result.data?.id, result.data?.pdfFileName));
						} else {
							console.error('Fail to save Order form');
						}
					})
					.finally(closeDrawer);
			}
		}
	};

	const processSaveAnswerData = (formData, hasUpdate = false, dataType) => {
		formData.referenceTemplate = formData.id;
		delete formData.id;
		formData.template = false;
		if (dataType === 'technologist') {
			formData.isRequiredViewImage = false;
		}
		formData.patient = data.patient;
		formData.study = data.study;

		axios
			.post(`${__config.data_sources.fhir}/Form`, formData)
			.then(async result => {
				if (result.status == '201') {
					console.log('Saving Form successfully');
					// Save pdf answer
					isPdfForm && (await savePdfForm(result.data?.id, result.data?.pdfFileName));
					hasUpdate && updateForm(getCompletedFormData(result));
				} else {
					console.error('Fail to save form');
				}
			})
			.finally(closeDrawer);
	};

	const savePdfForm = async (formId, pdfFileName) => {
		if (pdfInstance) {
			const { documentViewer, annotationManager } = pdfInstance.Core;
			await annotationManager.exportAnnotations().then(async xfdfString => {
				const doc = documentViewer.getDocument();
				await doc.getFileData({ xfdfString }).then(async data => {
					const arr = new Uint8Array(data);
					const uploadFileAnswer = new File([arr], pdfFileName, { type: 'application/pdf' });

					// Create blod url after save techform
					if (mavenTechFormMandatory && setPdfUrl) {
						const winURL = window.URL || window.webkitURL;
						const blobUrl = winURL.createObjectURL(uploadFileAnswer, { type: 'application/pdf' });
						setPdfUrl(blobUrl);
					}
					const fileData = new FormData();
					fileData.append('file', uploadFileAnswer);
					const resData = await uploadFileToServer(
						appModeIsPatientPortal
							? `${__config.data_sources.blume}/Form/attachment/${formId}?isReplaceFile=true`
							: `${__config.data_sources.fhir}/Form/attachment/${formId}?isReplaceFile=true`,
						fileData
					);
					if (resData?.length > 0) {
						console.log('Saving Form successfully');
					}
				});
			});
		}
	};

	const saveData = async (forceCloseDrawer = true, autoSave = false) => {
		let countOfUnAnsweredRequiredFields = 0;
		let validatedFormData = {};
		let firstErrorItemId = '';
		if (isPdfForm) {
			validatedFormData = { ...formData };
			validatedFormData.completed = true;
		} else if (!autoSave) {
			setValidateFormData(true);
			validatedFormData = await formUtils.validateForm(formData, true);
			validatedFormData.completed = true;
			countOfUnAnsweredRequiredFields = validatedFormData?.formTemplate?.content
				.flat(1)
				.filter(question => question?.error === true)?.length;

			// Scroll to first error item
			firstErrorItemId = validatedFormData?.formTemplate?.content
				.flat(1)
				.find(question => question?.error === true)?.id;
			if (firstErrorItemId) {
				const element = document.getElementById(firstErrorItemId);
				element?.scrollIntoView();
			}
		} else {
			validatedFormData = await removeErrorAttribute(formData);
		}
		if (countOfUnAnsweredRequiredFields === 0) {
			if (isOrderForm) {
				// For saving new Answer Order Form
				updateForm(res => processOrderSaveData(res, validatedFormData));
			} else if (!data.completed && data.type === 'technologist' && (!data.study || !mavenTechFormMandatory)) {
				// For saving new Answer Technologist Form
				if (!mavenTechFormMandatory || data.isRequiredViewImage) {
					updateForm(() => processSaveAnswerData(validatedFormData, false, data.type));
				} else {
					processSaveAnswerData(validatedFormData, true, data.type);
				}
			} else if (validatedFormData.template) {
				// For saving new Answer Other Blume Form
				processSaveAnswerData(validatedFormData, true);
			} else if ((!validatedFormData || validatedFormData.resourceType != 'BlumeForm') && !autoSave) {
				// For saving DocumentReference
				saveDocumentReference(studyId, patientId, appointmentId);
			} else {
				// For editing answer
				setSubmitInProgress(true);
				axios
					.put(
						`${useFhirUrl ? `${__config.data_sources.fhir}/` : __config.data_sources.blume}Form/${data.id}`,
						validatedFormData
					)
					.then(async result => {
						// Save pdf answer
						isPdfForm && (await savePdfForm(result.data?.id, result.data?.pdfFileName));
						const form = getCompletedFormData(result);
						if (updateForm) {
							updateForm(form);
						}
						if (forceCloseDrawer) {
							closeDrawer();
							setLastSyncedForm(form);
						}
						setSubmitInProgress(false);
					})
					.catch(err => {
						console.error('Error while form update', err);
						setSubmitInProgress(false);
					});
			}
		} else {
			setFormData(validatedFormData);
		}
	};

	const getCompletedFormData = result => ({
		id: result.data.id,
		patient: result.data?.patient,
		completed: result.data?.completed,
		organizationId: result.data.organizationId,
		status: result.data.status.charAt(0).toUpperCase() + result.data.status.slice(1),
		title: result.data.name,
		type: result.data.type,
		description: result.data.description,
		questionData: result.data.formTemplate?.content,
		resource: result.data,
		pdfFileName: result.data?.pdfFileName,
	});

	const autoSaveDebounce = useCallback(
		nextValue => {
			if (
				!submitInProgress &&
				!lastSyncedForm?.completed &&
				nextValue !== lastSyncedForm &&
				!nextValue?.completed
			) {
				saveData(false, true);
			}
		},

		[lastSyncedForm, submitInProgress]
	);

	/**
	 * Render Form Component
	 * @returns
	 */
	const renderFormComponent = () => (
		<FormTool
			noDefaultBanner
			banner={data?.resource?.formTemplate?.header?.banner}
			colour={data?.resource?.formTemplate?.header?.colour}
			descriptionColor="rgba(255, 255, 255, 0.6)"
			formDescription={data.description}
			formId={isPdfForm ? data.id : data.referenceTemplate ? data.referenceTemplate : data.id}
			formTitle={data.title}
			fromHistory={fromHistory}
			handleFormData={handleFormData}
			headerString={isOrderForm ? 'Order Form' : data.title}
			isCompact={isCompact}
			isReadOnly={data?.completed && !canEditWhenCompleted}
			isValidForm={isPdfForm ? true : isValidForm}
			logo={data?.resource?.formTemplate?.header?.logoImage}
			orderFormMapper={orderFormMapper}
			pageRef={pageRef}
			pdfHeight={pdfHeight}
			pdfUrl={pdfUrl}
			pdfWidth={pdfWidth}
			progressPercentage={`${progressPercentage}%`}
			questionsData={fromHistory ? formData?.formTemplate?.content : data.questionData}
			sectionName={sectionName}
			showBackBtn={showBackBtn}
			showCloseBtn={showCloseBtn}
			showEdit={showEdit}
			submitButtonContainerSx={submitButtonContainerSx}
			userSignature={userSignature}
			onCloseClick={closeDrawer}
			onEditClick={onEditClick}
			onSave={async () => await saveData(true, false)}
			onSubmit={onSubmit}
		/>
	);

	return isTablet ? (
		<MobileBottomDrawer fullHeight open={open} onClose={() => setOpen(false)}>
			<FormTool
				footer
				noDefaultBanner
				banner={data?.resource?.formTemplate?.header?.banner}
				colour={data?.resource?.formTemplate?.header?.colour}
				formDescription={data.description}
				formId={isPdfForm ? data.id : data.referenceTemplate ? data.referenceTemplate : data.id}
				formTitle={data.title}
				handleFormData={handleFormData}
				headerString={isOrderForm ? 'Order Form' : data.title}
				isCompact={isCompact}
				isReadOnly={data?.completed && !canEditWhenCompleted}
				isValidForm={isPdfForm ? true : isValidForm}
				logo={data?.resource?.formTemplate?.header?.logoImage}
				orderFormMapper={orderFormMapper}
				pdfHeight="82vh"
				pdfUrl={pdfUrl}
				progressPercentage={`${progressPercentage}%`}
				questionsData={data.questionData}
				sectionName={sectionName}
				userSignature={userSignature}
				onCloseClick={closeDrawer}
				onSave={saveData}
				onSubmit={onSubmit}
			/>
		</MobileBottomDrawer>
	) : fromHistory || !onDrawer ? (
		<div ref={ref} data-testId="fullscreen" style={{ width: drawerWidth || 'auto' }}>
			{renderFormComponent()}
		</div>
	) : (
		<Drawer
			PaperProps={{
				sx: {
					background: drawerColor,
					boxSizing: 'border-box',
					maxWidth: '100vw',
					left: anchor === 'left' ? '64px' : null,
					overflow: 'scroll',
					width: '850px',
					[mediaDesktopL]: {
						width: '652px',
					},
				},
			}}
			anchor={anchor}
			data-testid="form-drawer"
			open={open}
			sx={
				data.type == 'technologist' && {
					right: 'unset',
					'.MuiBackdrop-root': {
						right: 'unset',
					},
				}
			}
			variant={isOrderForm ? 'persistent' : 'temporary'}
		>
			<div ref={ref}>{renderFormComponent()}</div>
		</Drawer>
	);
};

export default FormDrawer;
